import {MixedPanel} from './models/mixed-panel';
import {BackbasePanelLoadService} from './services/backbase-panel-load.service';
import {EvoPanelLoadService} from './services/evo-panel-load.service';
import {MixedPanelState} from './models/mixed-panel-state';
import {BackbaseLauncherDeckContainer} from './models/backbase-launcher-deck-container';
import {SimpleEmitter} from './models/simple-emitter';
import {EvoContainerEventEmitter} from './models/evo-container-event-emitter';

type NavigationVoterCallback = (event: Event, panelLoadFunction: Function) => boolean;
type HistoryPushStateFunction = (data: any, title: string, url?: string | null) => void;

export class EvoContainer {
    private backbasePanelLoadService = new BackbasePanelLoadService();
    private evoPanelLoadService = new EvoPanelLoadService();
    private panels: Array<MixedPanel> = [];
    private navigationVoters: Array<NavigationVoterCallback> = [];
    private lastHash: string = "";
    private ignoreEvoWidgets: boolean;
    private defaultPanelToshow: MixedPanel;

    constructor(ignoreEvoWidgets: boolean = false) {
        this.ignoreEvoWidgets = ignoreEvoWidgets;
    }

    public init(ldc: BackbaseLauncherDeckContainer) {
        this.registerNavigationVoters();
        this.loadPanels(ldc).then(() => {
            this.defaultPanelToshow = this.panels.find(panel => panel.defaultPanelToShow());
            this.sortPanels();
            this.registerHashChangeEventListener();
            // Trigger a default navigation
            this.voteAndNavigate();
        });
    }

    public addNavigationVoter(voter: NavigationVoterCallback) {
        this.navigationVoters.push(voter)
    }

    private registerNavigationVoters() {
        // Register any callback functions
        const topic = 'DB_LAUNCHER_DECK_CONTAINER_ADD_HASHCHANGE_CALLBACK';
        (window as any).gadgets.pubsub.subscribe(topic, (cb: NavigationVoterCallback) => this.addNavigationVoter(cb));
    }

    private async loadPanels(ldc: BackbaseLauncherDeckContainer) {
        const bbPanels = await this.backbasePanelLoadService.loadPanels(ldc);
        if (!this.ignoreEvoWidgets) {
            const evoPanels = await this.evoPanelLoadService.loadPanels(ldc);
            this.panels.push(...evoPanels);
        }
        this.panels.push(...bbPanels);
    }

    private registerHashChangeEventListener() {
        window.addEventListener('hashchange', event => {
            this.voteAndNavigate(event);
        });

        /* Also trigger voteAndNavigate when navigating within one widget.
            Angular router does not trigger the hashchange event. */
        history.pushState = this.monkeyPatchHistoryPushState(history.pushState);
    }

    private monkeyPatchHistoryPushState(originalHistoryPushStateFunction: HistoryPushStateFunction): HistoryPushStateFunction {
        const self = this;
        return function () {
            var ret = originalHistoryPushStateFunction.apply(window, arguments);
            if (arguments[2].indexOf('/evo#/messages') >= 0 || arguments[2].indexOf('/evo#/diy-form') >= 0) {
                const event = document.createEvent('Event');
                event.initEvent('pushstate', true, true);
                self.voteAndNavigate(event);
            }
            return ret;
        };
    }

    private sortPanels() {
        /*
            Eg. match one panel with #/payments/payment/new
            --> Issue if we have one panel with hash '#/payment' and another with '#/payments'
            --> Solution: sort by longest first

            #/diy-menu/menu/overview
            #/accounts/transactions-history
            #/accounts/future-transactions
            #/accounts/open-account
            #/payment/confirmation
            #/accounts/overview
            #/payment/summary
            #/card-linkage
            #/investments
            #/settings
            #/messages
            #/payments
            #/profile
            #/contact
            #/payment
            #/zoomit
            #/inbox
            #/esafe
        */
        // Sort with longest first
        this.panels.sort((a, b) => {
            if (a.hash.length < b.hash.length) return 1;
            if (a.hash.length > b.hash.length) return -1;
            return 0;
        });
    }


    private findPanel(hash: string): MixedPanel {
        return this.panels.find(panel => hash.startsWith(panel.hash));
    }

    private getPanelByHash(hash: string): MixedPanel {        
        const defaultHash = '#/accounts/overview';
        if (!hash) {
            return this.defaultPanelToshow || this.findPanel(defaultHash);
        } else {
            const panelToBeShownByHash = this.findPanel(hash);
            if (panelToBeShownByHash) {                
                return panelToBeShownByHash;
            }  
            else if (!this.lastHash){
                return this.defaultPanelToshow || this.findPanel(defaultHash);
            }
        }
    }

    private voteAndNavigate(event?: Event): void {

        const newHash = window.location.hash;
        const panelToNavigateTo: MixedPanel = this.getPanelByHash(newHash);

        if (!panelToNavigateTo) {
            // The hash was not valid
            return;
        }

        const previousPanel = this.getPanelByHash(this.lastHash);

        const showPanelFunction = (shouldTriggerWidgetActivation: boolean) => {
            if (this.lastHash != newHash && previousPanel) {
                previousPanel.hide();
                this.emit(MixedPanelState.STATE_DESTROYED, previousPanel);
            }

            this.lastHash = window.location.hash;

            if (this.evoPanelLoadService.getPanelToAlwaysLoad().length) {
                this.evoPanelLoadService.getPanelToAlwaysLoad().forEach((panel: MixedPanel) => {
                    panel.show();
                })
            }
            if (shouldTriggerWidgetActivation) {
                this.emit(MixedPanelState.STATE_BEFORE_SHOWING, panelToNavigateTo);
            }
            panelToNavigateTo.show();
        };

        // Don't vote if we have no HashChangeEvent -- this means we are doing the initial/default navigation
        let isSomeOneOpposed = event && this.navigationVoters.some(navigationVoter => !navigationVoter(event, showPanelFunction.bind(this)));
        if (!isSomeOneOpposed) {
            showPanelFunction(!event || event.type !== 'pushstate');
        }
    }

    public stateEmitter = new SimpleEmitter() as EvoContainerEventEmitter;

    private emit(panelState: MixedPanelState, panel: MixedPanel) {
        const pubsub = (window as any).gadgets.pubsub;
        switch (panelState) {
            case MixedPanelState.STATE_DESTROYED:
                const payload = {
                    oldHash: this.lastHash,
                    newHash: window.location.hash,
                    oldPanelId: panel.id
                };
                pubsub.publish(
                    'DB_LAUNCHER_DECK_CONTAINER_DESTROY_PANEL_ON_HASHCHANGE',
                    payload
                );
                this.stateEmitter.emit({
                    state: MixedPanelState.STATE_DESTROYED,
                    destroyEvent: payload
                });
                break;
            case MixedPanelState.STATE_BEFORE_SHOWING:
                pubsub.publish('DB_BEFORE_PANEL_LOAD', panel.id);
                this.stateEmitter.emit({
                    state: MixedPanelState.STATE_BEFORE_SHOWING,
                    beforeShowEvent: {panelId: panel.id}
                });
                break;
        }
    }

}
